import {
  FriendEventSchema,
  type FriendPositions,
  FriendPositionsSchema,
  FriendRejectedSchema,
  FriendRequestedSchema,
  FriendsStateSchema,
  PartyEventSchema,
  PartyInvitedSchema,
  PartyRejectedSchema,
  type PartyState,
  PartyStateSchema,
} from '@shefira/shared';

import { AllianceClient, alliedParty } from './alliance-client.js';
import { BlockClient } from './block-client.js';
import type { ChatPanel, ChatTarget } from './chat.js';
import { CHAT_HELP, type ChatCommand, MODERATION_HELP } from './chat-commands.js';
import { GuildClient } from './guild-client.js';
import { ModerationClient } from './moderation-client.js';
import { TradeClient, type TradeClientDeps } from './trade-client.js';
import { FriendsPanel } from './friends-panel.js';
import { InvitePrompt } from './invite-prompt.js';
import { PartyFrames } from './party-frames.js';
import { PlayerMenu } from './player-menu.js';
import {
  FRIEND_REFUSAL_TEXT,
  friendEventText,
  PARTY_REFUSAL_TEXT,
  partyEventText,
} from './social-text.js';
import { uiFormat, uiText } from './text.js';

/** The little of a Colyseus room this needs. */
interface SocialRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/**
 * The client's side of the social game (ADR 0032, ADR 0033): shows what the
 * server says — the party's frames, the friends' list, invitations and
 * requests, what happened — and turns clicks and chat lines into
 * intentions. It keeps nothing of its own beyond the last lists sent.
 */
export class SocialClient {
  private party: PartyState = { leaderId: null, members: [] };
  private readonly frames: PartyFrames;
  private readonly friends: FriendsPanel;
  private readonly partyPrompt: InvitePrompt;
  private readonly friendPrompt: InvitePrompt;
  private readonly menu = new PlayerMenu();
  /** Guilds (ADR 0036), trades (ADR 0034) and sanctions (ADR 0037) share the room, the chat and the player menu. */
  readonly guild: GuildClient;
  readonly trade: TradeClient;
  private readonly moderation: ModerationClient;
  /** Two parties joined for Shefira's raid (ADR 0068 §5). */
  private readonly alliance: AllianceClient;
  /** Players this character blocked (ADR 0076): the chat hides their lines. */
  readonly blocks: BlockClient;

  constructor(
    private readonly room: SocialRoom,
    private readonly chat: ChatPanel,
    /** The local character's id and zone, read when needed: both arrive after the join. */
    private readonly self: () => { characterId: string; zoneId: string },
    /** The friends in this zone moved: the map and the minimap draw them. */
    onFriendPositions: (positions: FriendPositions['positions']) => void,
    /** What a trade needs of the scene: the bag, its window and the icon atlas. */
    tradeDeps: TradeClientDeps,
    /** Whether the player is a game master: the help then lists the sanctions. */
    private readonly isAdmin: () => boolean = () => false,
  ) {
    this.guild = new GuildClient(room, chat, { selfId: () => this.self().characterId });
    this.trade = new TradeClient(room, tradeDeps);
    this.moderation = new ModerationClient(room, chat);
    this.blocks = new BlockClient(room, chat);
    this.frames = new PartyFrames({
      onLeave: () => {
        room.send('party_leave', {});
      },
      onKick: (characterId) => {
        room.send('party_kick', { characterId });
      },
      onLeaveAlliance: () => {
        this.alliance.leave();
      },
    });
    this.alliance = new AllianceClient(room, chat, () => {
      this.showAlliance();
    });
    this.friends = new FriendsPanel({
      onAdd: (name) => {
        room.send('friend_request', { name });
      },
      onMessage: (name) => {
        this.whisperTo(name);
      },
      onInvite: (name) => {
        room.send('party_invite', { name });
      },
      onRemove: (characterId) => {
        room.send('friend_remove', { characterId });
      },
      onUnblock: (name) => {
        this.blocks.unblock(name);
      },
    });
    this.blocks.onChange(() => {
      this.friends.setBlocked(this.blocks.list);
    });
    this.partyPrompt = new InvitePrompt((from, accept) => {
      room.send('party_answer', { from, accept });
    });
    this.friendPrompt = new InvitePrompt((from, accept) => {
      room.send('friend_answer', { from, accept });
    });

    room.onMessage('party', (payload) => {
      const parsed = PartyStateSchema.safeParse(payload);
      if (!parsed.success) return;
      this.party = parsed.data;
      const { characterId, zoneId } = this.self();
      this.frames.show(parsed.data, characterId, zoneId);
      this.showAlliance();
      // Out of the party, the chat stops speaking to it.
      if (parsed.data.members.length === 0 && chat.current.channel === 'party') {
        chat.setTarget({ channel: 'zone' });
      }
    });
    room.onMessage('party_invited', (payload) => {
      const parsed = PartyInvitedSchema.safeParse(payload);
      if (!parsed.success) return;
      this.partyPrompt.show({
        from: parsed.data.from,
        text: uiFormat("{name} t'invite dans son groupe.", { name: parsed.data.name }),
        expiresInMs: parsed.data.expiresInMs,
      });
    });
    room.onMessage('party_event', (payload) => {
      const parsed = PartyEventSchema.safeParse(payload);
      if (parsed.success) chat.notice(partyEventText(parsed.data));
    });
    room.onMessage('party_rejected', (payload) => {
      const parsed = PartyRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(PARTY_REFUSAL_TEXT[parsed.data.reason]);
    });

    room.onMessage('friends', (payload) => {
      const parsed = FriendsStateSchema.safeParse(payload);
      if (parsed.success) this.friends.setFriends(parsed.data);
    });
    room.onMessage('friend_positions', (payload) => {
      const parsed = FriendPositionsSchema.safeParse(payload);
      if (parsed.success) onFriendPositions(parsed.data.positions);
    });
    room.onMessage('friend_requested', (payload) => {
      const parsed = FriendRequestedSchema.safeParse(payload);
      if (!parsed.success) return;
      this.friendPrompt.show({
        from: parsed.data.from,
        text: uiFormat('{name} veut devenir ton ami.', { name: parsed.data.name }),
        expiresInMs: parsed.data.expiresInMs,
      });
    });
    room.onMessage('friend_event', (payload) => {
      const parsed = FriendEventSchema.safeParse(payload);
      if (parsed.success) chat.notice(friendEventText(parsed.data));
    });
    room.onMessage('friend_rejected', (payload) => {
      const parsed = FriendRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(FRIEND_REFUSAL_TEXT[parsed.data.reason]);
    });
  }

  /** A click on another player: the menu of what one may ask of them. */
  openPlayerMenu(name: string, x: number, y: number): void {
    this.menu.open(
      name,
      [
        {
          label: 'Inviter dans le groupe',
          run: () => {
            this.room.send('party_invite', { name });
          },
        },
        {
          label: 'Inspecter',
          run: () => {
            this.room.send('inspect', { name });
          },
        },
        {
          label: 'Message privé',
          run: () => {
            this.whisperTo(name);
          },
        },
        {
          label: 'Ajouter en ami',
          run: () => {
            this.room.send('friend_request', { name });
          },
        },
        {
          label: 'Échanger',
          run: () => {
            this.trade.request(name);
          },
        },
        // A party's leader proposes to another party's leader (ADR 0068 §5).
        ...(this.leads()
          ? [
              {
                label: 'Proposer une alliance',
                run: () => {
                  this.alliance.invite(name);
                },
              },
            ]
          : []),
        ...(this.guild.canInvite()
          ? [
              {
                label: 'Inviter dans la guilde',
                run: () => {
                  this.guild.invite(name);
                },
              },
            ]
          : []),
        {
          label: 'Signaler',
          run: () => {
            this.moderation.report(name);
          },
        },
        this.blocks.isBlocked(name)
          ? {
              label: 'Débloquer',
              run: () => {
                this.blocks.unblock(name);
              },
            }
          : {
              label: 'Bloquer',
              run: () => {
                this.blocks.block(name);
              },
            },
      ],
      x,
      y,
    );
  }

  /** A chat line, typed while the chat spoke on `target`. */
  run(command: ChatCommand, target: ChatTarget, links: readonly string[] = []): void {
    // Items linked in the line (ADR 0085), carried only by what is said.
    const linked = links.length > 0 ? { links: [...links] } : {};
    switch (command.kind) {
      case 'say': {
        const channel = command.channel ?? target.channel;
        const to = channel === 'private' ? (command.to ?? target.to) : undefined;
        if (channel === 'private' && to === undefined) {
          this.chat.notice('À qui ? Tape /m nom message.');
          return;
        }
        this.room.send('chat', {
          channel,
          text: command.text,
          ...(to !== undefined && { to }),
          ...linked,
        });
        return;
      }
      case 'switch':
        if (command.channel === 'party' && this.party.members.length === 0) {
          this.chat.notice("Tu n'es pas dans un groupe.");
          return;
        }
        if (command.channel === 'guild' && this.guild.state === null) {
          this.chat.notice("Tu n'es pas dans une guilde.");
          return;
        }
        this.chat.setTarget({
          channel: command.channel,
          ...(command.to !== undefined && { to: command.to }),
        });
        return;
      case 'reply': {
        const to = this.chat.replyTo;
        if (to === undefined) {
          this.chat.notice("Personne ne t'a encore écrit.");
          return;
        }
        if (command.text === undefined) this.chat.setTarget({ channel: 'private', to });
        else this.room.send('chat', { channel: 'private', to, text: command.text, ...linked });
        return;
      }
      case 'invite':
        this.room.send('party_invite', { name: command.name });
        return;
      case 'leave':
        this.room.send('party_leave', {});
        return;
      case 'kick': {
        const wanted = command.name.toLowerCase();
        const member = this.party.members.find((m) => m.name.toLowerCase() === wanted);
        if (member === undefined)
          this.chat.notice(uiFormat("{name} n'est pas dans ton groupe.", { name: command.name }));
        else this.room.send('party_kick', { characterId: member.id });
        return;
      }
      case 'friend':
        this.room.send('friend_request', { name: command.name });
        return;
      case 'trade':
        this.trade.request(command.name);
        return;
      case 'block':
        this.blocks.block(command.name);
        return;
      case 'unblock':
        if (!this.blocks.unblock(command.name)) {
          this.chat.notice(uiFormat("{name} n'est pas bloqué.", { name: command.name }));
        }
        return;
      case 'away':
        this.room.send('set_away', {});
        this.chat.notice('Tu es absent. Ta prochaine action te ramène.');
        return;
      case 'guild':
        this.guild.run(command);
        return;
      case 'moderate':
        this.moderation.moderate(command);
        return;
      case 'report':
        this.moderation.report(command.name);
        return;
      case 'event':
        // The server checks the flag; a player who is not a game master is told no.
        this.room.send(
          'admin_command',
          command.action === 'start'
            ? { action: 'start_event', kind: command.event, biome: command.biome }
            : { action: 'stop_event' },
        );
        return;
      case 'help':
        this.chat.notice(CHAT_HELP);
        if (this.isAdmin()) this.chat.notice(MODERATION_HELP);
        return;
      case 'unknown':
        this.chat.notice(
          uiFormat('Commande inconnue : /{command}. {help}', {
            command: command.command,
            help: uiText(CHAT_HELP),
          }),
        );
        return;
    }
  }

  /** Whether the local character leads a party of more than themselves. */
  private leads(): boolean {
    return this.party.members.length > 1 && this.party.leaderId === this.self().characterId;
  }

  private showAlliance(): void {
    const ally = alliedParty(this.alliance.current, this.party.leaderId);
    const selfId = this.self().characterId;
    const canLeave = this.alliance.current.parties.some((party) => party.leaderId === selfId);
    this.frames.showAlliance(ally ?? null, canLeave);
  }

  private whisperTo(name: string): void {
    this.chat.setTarget({ channel: 'private', to: name });
    this.chat.focus();
  }

  destroy(): void {
    this.guild.destroy();
    this.trade.destroy();
    this.moderation.destroy();
    this.alliance.destroy();
    this.frames.destroy();
    this.friends.destroy();
    this.partyPrompt.close();
    this.friendPrompt.close();
    this.menu.close();
  }
}
